import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { buyerCommunicationsService } from '../../../services/buyerService';

const CommunicationsTab = ({ language }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedComm, setSelectedComm] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const content = {
    en: {
      title: 'Communications',
      noCommunications: 'No communications yet',
      loading: 'Loading communications...',
      subject: 'Subject',
      supplier: 'Supplier',
      status: 'Status',
      date: 'Date',
      viewThread: 'View Thread',
      backToList: 'Back to List',
      sendMessage: 'Send Message',
      typeMessage: 'Type your message...',
      sending: 'Sending...',
      pending: 'Pending',
      responded: 'Responded',
      closed: 'Closed'
    },
    zh: {
      title: '沟通',
      noCommunications: '尚无沟通记录',
      loading: '加载沟通中...',
      subject: '主题',
      supplier: '供应商',
      status: '状态',
      date: '日期',
      viewThread: '查看对话',
      backToList: '返回列表',
      sendMessage: '发送消息',
      typeMessage: '输入您的消息...',
      sending: '发送中...',
      pending: '待处理',
      responded: '已回复',
      closed: '已关闭'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    const controller = new AbortController();
    loadCommunications(controller.signal);
    return () => controller.abort();
  }, []);

  const loadCommunications = async (signal) => {
    try {
      setLoading(true);
      setError('');
      const data = await buyerCommunicationsService?.getAll();
      if (signal?.aborted) return;
      setCommunications(data);
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.message !== 'signal is aborted without reason') {
        setError(err?.message || 'Failed to load communications');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedComm) return;

    try {
      setSending(true);
      await buyerCommunicationsService?.addMessage(selectedComm?.id, newMessage);
      setNewMessage('');
      await loadCommunications();
      const updated = communications?.find(c => c?.id === selectedComm?.id);
      setSelectedComm(updated);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      responded: 'text-primary',
      closed: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getStatusBg = (status) => {
    const colors = {
      pending: 'bg-warning/10',
      responded: 'bg-primary/10',
      closed: 'bg-muted'
    };
    return colors?.[status] || 'bg-muted';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">{t?.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (selectedComm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => setSelectedComm(null)}
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
          >
            {t?.backToList}
          </Button>
        </div>

        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-foreground mb-1">{selectedComm?.subject}</h3>
              <p className="text-sm text-muted-foreground">
                {t?.supplier}: {selectedComm?.supplier?.[`name${language === 'zh' ? 'Zh' : 'En'}`]}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(selectedComm?.status)} ${getStatusColor(selectedComm?.status)}`}>
              {t?.[selectedComm?.status] || selectedComm?.status?.toUpperCase()}
            </span>
          </div>

          <div className="space-y-3 mb-4">
            {selectedComm?.messages?.map((msg) => (
              <div key={msg?.id} className="bg-card rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium">{msg?.sender?.fullName}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(msg?.createdAt)?.toLocaleString()}
                  </span>
                </div>
                <p className="text-sm">{msg?.message}</p>
              </div>
            ))}
          </div>

          {selectedComm?.status !== 'closed' && (
            <form onSubmit={handleSendMessage} className="space-y-3">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder={t?.typeMessage}
              />
              <Button
                type="submit"
                loading={sending}
                iconName="Send"
                disabled={!newMessage?.trim()}
              >
                {sending ? t?.sending : t?.sendMessage}
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-heading font-bold text-foreground">{t?.title}</h2>

      {communications?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Mail" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noCommunications}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {communications?.map((comm) => (
            <div key={comm?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-foreground mb-1">{comm?.subject}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t?.supplier}: {comm?.supplier?.[`name${language === 'zh' ? 'Zh' : 'En'}`]}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(comm?.status)} ${getStatusColor(comm?.status)}`}>
                  {t?.[comm?.status] || comm?.status?.toUpperCase()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {new Date(comm?.createdAt)?.toLocaleDateString()}
                </span>
                <Button
                  onClick={() => setSelectedComm(comm)}
                  variant="outline"
                  size="sm"
                  iconName="MessageSquare"
                >
                  {t?.viewThread}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunicationsTab;